import { useState, useEffect, useCallback, useRef } from "react";
import { Heart, Trash2, Pin, PinOff, Send, MessageCircle, Users, Shield, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "zojuist";
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  return new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
}

function rolLabel(rol) {
  const map = { admin: "Admin", company_admin: "Beheerder", trainer: "Trainer", sporter: "Sporter", user: "Cursist" };
  return map[rol] || rol;
}
function rolColor(rol) {
  const map = { admin: "#C9A84C", company_admin: "#C9A84C", trainer: "#60a5fa", sporter: "#4ade80", user: "#a78bfa" };
  return map[rol] || "#9ca3af";
}

// ── Post kaart ────────────────────────────────────────────────────────────────
function PostCard({ post, currentUserId, currentRole, onLike, onDelete, onPin, showBedrijf }) {
  const canDelete = post.author_id === currentUserId || ["admin", "company_admin", "trainer"].includes(currentRole);
  const canPin = ["admin", "company_admin"].includes(currentRole);
  const isLiked = post.likes_door?.includes(currentUserId);

  return (
    <div data-testid={`post-${post.id}`}
      className={`bg-[#111] border rounded-2xl p-4 transition-all ${post.gepind ? "border-[#C9A84C]/40 shadow-[0_0_0_1px_rgba(201,168,76,0.15)]" : "border-white/8"}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black text-black shrink-0"
            style={{ background: rolColor(post.author_rol) }}>
            {(post.author_naam || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-white text-sm font-bold truncate">{post.author_naam}</p>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold"
                style={{ background: rolColor(post.author_rol) + "22", color: rolColor(post.author_rol) }}>
                {rolLabel(post.author_rol)}
              </span>
              {post.gepind && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#C9A84C]/15 text-[#C9A84C] font-semibold flex items-center gap-1">
                  <Pin size={8} /> Gepind
                </span>
              )}
            </div>
            <p className="text-zinc-600 text-xs mt-0.5">
              {timeAgo(post.aangemaakt_op)}
              {showBedrijf && post.bedrijf_naam && <span className="ml-1.5 text-zinc-700">· {post.bedrijf_naam}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {canPin && (
            <button onClick={() => onPin(post.id)} data-testid={`pin-post-${post.id}`}
              className="p-1.5 rounded-lg text-zinc-600 hover:text-[#C9A84C] hover:bg-[#C9A84C]/10 transition-all">
              {post.gepind ? <PinOff size={13} /> : <Pin size={13} />}
            </button>
          )}
          {canDelete && (
            <button onClick={() => onDelete(post.id)} data-testid={`delete-post-${post.id}`}
              className="p-1.5 rounded-lg text-zinc-600 hover:text-red-400 hover:bg-red-500/10 transition-all">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
      <p className="text-zinc-200 text-sm leading-relaxed whitespace-pre-line">{post.tekst}</p>
      <div className="flex items-center gap-3 mt-3 pt-3 border-t border-white/5">
        <button onClick={() => onLike(post.id)} data-testid={`like-post-${post.id}`}
          className={`flex items-center gap-1.5 text-xs font-semibold transition-colors ${isLiked ? "text-red-400" : "text-zinc-500 hover:text-red-400"}`}>
          <Heart size={13} fill={isLiked ? "currentColor" : "none"} />
          {post.likes || 0}
        </button>
      </div>
    </div>
  );
}

// ── Hoofd component ───────────────────────────────────────────────────────────
export default function CommunityTab({ token, currentUserId, currentRole, showBedrijf = false }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tekst, setTekst] = useState("");
  const [posting, setPosting] = useState(false);
  const textRef = useRef(null);

  const apiBase = showBedrijf ? `${API}/api/admin/community` : `${API}/api/community/posts`;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(apiBase, { headers: { Authorization: `Bearer ${token}` } });
      if (r.ok) setPosts((await r.json()).posts || []);
    } catch {}
    setLoading(false);
  }, [token, apiBase]);

  useEffect(() => { load(); }, [load]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!tekst.trim()) return;
    setPosting(true);
    try {
      const r = await fetch(`${API}/api/community/posts`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ tekst }),
      });
      if (r.ok) {
        setTekst("");
        load();
      }
    } catch {}
    setPosting(false);
  };

  const handleLike = async (postId) => {
    try {
      const r = await fetch(`${API}/api/community/posts/${postId}/like`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: d.likes, likes_door: d.geliked ? [...(p.likes_door || []), currentUserId] : (p.likes_door || []).filter(id => id !== currentUserId) } : p));
      }
    } catch {}
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Post verwijderen?")) return;
    try {
      await fetch(`${API}/api/community/posts/${postId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(prev => prev.filter(p => p.id !== postId));
    } catch {}
  };

  const handlePin = async (postId) => {
    try {
      const r = await fetch(`${API}/api/community/posts/${postId}/pin`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const d = await r.json();
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, gepind: d.gepind } : p));
      }
    } catch {}
  };

  return (
    <div className="max-w-2xl" data-testid="community-tab">
      <div className="mb-6">
        <p className="text-[#C9A84C] text-xs font-bold uppercase tracking-widest mb-1">
          {showBedrijf ? "Alle bedrijven" : "Jouw community"}
        </p>
        <h2 className="text-xl font-bold text-white">Community</h2>
        <p className="text-zinc-500 text-sm mt-1">
          {showBedrijf ? "Overzicht van alle bedrijfsfeeds" : "Deel updates, aankondigingen en motivatie met je community"}
        </p>
      </div>

      {/* Nieuwe post */}
      {!showBedrijf && (
        <form onSubmit={handlePost} className="bg-[#111] border border-white/8 rounded-2xl p-4 mb-6">
          <textarea
            ref={textRef}
            value={tekst}
            onChange={e => setTekst(e.target.value)}
            onKeyDown={e => e.key === "Enter" && e.ctrlKey && handlePost(e)}
            placeholder="Deel iets met je community..."
            data-testid="post-input"
            rows={3}
            className="w-full bg-transparent text-white text-sm placeholder-zinc-600 resize-none focus:outline-none"
          />
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/5">
            <span className="text-zinc-600 text-xs">{tekst.length}/2000 · Ctrl+Enter om te posten</span>
            <button type="submit" disabled={posting || !tekst.trim()}
              data-testid="verstuur-post-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-black text-sm font-bold disabled:opacity-40"
              style={{ background: "#C9A84C" }}>
              <Send size={13} /> {posting ? "Plaatsen..." : "Plaatsen"}
            </button>
          </div>
        </form>
      )}

      {/* Posts */}
      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-28 bg-[#111] rounded-2xl animate-pulse" />)}</div>
      ) : posts.length === 0 ? (
        <div className="bg-[#111] border border-white/8 rounded-2xl p-10 text-center">
          <MessageCircle size={28} className="text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-400 font-semibold mb-1">Nog geen posts</p>
          <p className="text-zinc-600 text-sm">Wees de eerste die iets deelt met de community!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(p => (
            <PostCard
              key={p.id}
              post={p}
              currentUserId={currentUserId}
              currentRole={currentRole}
              onLike={handleLike}
              onDelete={handleDelete}
              onPin={handlePin}
              showBedrijf={showBedrijf}
            />
          ))}
        </div>
      )}
    </div>
  );
}
